/**
 * 微服务发现和数据抓取服务
 * 用于从其他微服务获取信息和数据
 */

import { ServiceDiscovery, ServiceInfo } from '@ms/shared-auth';
import msAiAuthConfig from '../config/auth';

// 创建服务发现实例
const serviceDiscovery = new ServiceDiscovery({
  msLoginUrl: msAiAuthConfig.msLoginUrl || 'https://login.ihoms.cn',
  timeout: 10000,
  cacheTimeout: 60000, // 1分钟缓存
});

export interface ServiceDataFetchOptions {
  serviceName: string;
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  params?: Record<string, any>;
  body?: any;
  headers?: Record<string, string>;
}

export interface ServiceDataFetchResult {
  success: boolean;
  data?: any;
  error?: string;
  statusCode?: number;
  duration?: number;
}

class ServiceDiscoveryService {
  /**
   * 获取所有微服务列表
   */
  async getAllServices(): Promise<ServiceInfo[]> {
    try {
      return await serviceDiscovery.getAllServices();
    } catch (error: any) {
      // 不抛出错误，让调用方处理（可以返回 Mock 数据）
      // 只在控制台输出警告，不显示错误提示
      console.warn('获取微服务列表失败（可能是 CORS 错误）:', error.message || error);
      throw error; // 重新抛出，让调用方的 catch 处理
    }
  }

  /**
   * 获取特定微服务信息
   */
  async getService(serviceName: string): Promise<ServiceInfo | null> {
    try {
      return await serviceDiscovery.getService(serviceName);
    } catch (error: any) {
      console.error(`获取微服务 ${serviceName} 信息失败:`, error);
      throw new Error(`获取微服务信息失败: ${error.message}`);
    }
  }

  /**
   * 检查微服务是否在线
   */
  async checkServiceHealth(serviceName: string): Promise<boolean> {
    try {
      return await serviceDiscovery.isServiceOnline(serviceName);
    } catch (error: any) {
      console.error(`检查微服务 ${serviceName} 健康状态失败:`, error);
      return false;
    }
  }

  /**
   * 从微服务抓取数据
   */
  async fetchServiceData(
    options: ServiceDataFetchOptions
  ): Promise<ServiceDataFetchResult> {
    const startTime = Date.now();
    
    try {
      // 1. 获取微服务信息
      const service = await this.getService(options.serviceName);
      if (!service) {
        return {
          success: false,
          error: `微服务 ${options.serviceName} 不存在`,
        };
      }

      if (!service.backendUrl) {
        return {
          success: false,
          error: `微服务 ${options.serviceName} 没有后端地址`,
        };
      }

      // 2. 构建请求 URL
      let url = `${service.backendUrl}${options.endpoint}`;
      
      // 添加查询参数
      if (options.params && Object.keys(options.params).length > 0) {
        const params = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
          }
        });
        const queryString = params.toString();
        if (queryString) {
          url += (url.includes('?') ? '&' : '?') + queryString;
        }
      }

      // 3. 构建请求配置
      const requestOptions: RequestInit = {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      };

      // 添加请求体
      if (options.body && (options.method === 'POST' || options.method === 'PUT')) {
        requestOptions.body = JSON.stringify(options.body);
      }

      // 4. 发送请求
      console.log(`🌐 从微服务 ${options.serviceName} 抓取数据:`, url);
      const response = await fetch(url, requestOptions);
      const duration = Date.now() - startTime;

      // 5. 解析响应
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        return {
          success: false,
          error: `请求失败: ${response.status} ${response.statusText}`,
          statusCode: response.status,
          duration,
        };
      }

      return {
        success: true,
        data,
        statusCode: response.status,
        duration,
      };
    } catch (error: any) {
      const duration = Date.now() - startTime;
      console.error(`从微服务 ${options.serviceName} 抓取数据失败:`, error);
      return {
        success: false,
        error: error.message || '未知错误',
        duration,
      };
    }
  }

  /**
   * 清除缓存
   */
  clearCache(): void {
    serviceDiscovery.clearCache();
  }

  /**
   * 清除特定服务的缓存
   */
  clearServiceCache(serviceName: string): void {
    serviceDiscovery.clearServiceCache(serviceName);
  }
}

export const serviceDiscoveryService = new ServiceDiscoveryService();
export default serviceDiscoveryService;
